import React from "react";
import { PartnerPackageWithExpandedProducts } from "types";
import { NoResults } from "../Common/NoResults";
import { PackageTile } from "./PackageTile";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useSearch } from "@/hooks/useSearch";
import { getExpandedExperiencesInPackage } from "@/models/Package";
import { ProductSkeleton } from "@/pages/Product/SelectExperiencePage";

export const PartnerPackageList = ({ search }: { search: string }) => {
    const title = "Partner Packages";
    const { data: partnerPackages, isLoading: isLoadingPartnerPackages } =
        useAutoFetchCollection<PartnerPackageWithExpandedProducts>("partnerPackages", {
            expand: "selectedProducts,seller.name",
        });
    const seller = useSeller();

    const filteredPartnerPackages = useSearch(
        partnerPackages,
        (pkg) => [pkg.name?.trim(), getPartnerSellerNamesForSelectedProducts(seller.id, pkg)],
        search || "",
    );

    if (isLoadingPartnerPackages) {
        return <ProductSkeleton header={title} />;
    }

    return (
        <div className="px-0 py-10">
            <div className="text-lg font-bold">{title}</div>
            {filteredPartnerPackages.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2" data-testid="experience-list">
                    {filteredPartnerPackages.map((pkg) => (
                        <PackageTile
                            key={pkg.id}
                            isPartner
                            pkg={pkg}
                            experiences={getExpandedExperiencesInPackage(pkg)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const getPartnerSellerNamesForSelectedProducts = (sellerId: string, pkg: PartnerPackageWithExpandedProducts) => {
    return pkg.selectedProducts
        .filter(
            // Skip selected products that are from the same seller
            (selectedProduct) => selectedProduct.product.seller.id !== sellerId,
        )
        .map((selectedProduct) => {
            const seller = selectedProduct.product.seller;
            return seller?.name?.trim() ?? seller?.id ?? "";
        })
        .filter(Boolean)
        .join(", ");
};
